import React, { FC } from 'react';
import { connect } from 'react-redux';

import type { CurrencyCode } from 'app/api/services/site-configs/types';
import type { ReduxState } from 'app/redux/types';

import { Amount } from 'app/design-system/components/Amount';
import { Hint } from 'app/design-system/components/Popper';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import selectCurrency from 'app/redux/selectors/сurrency';

import { RecordOfBigStake, RecordOfEntryErrorType } from '../../../records';
import {
  selectBigStakeByDialogId,
  selectBigStakeErrorByDialogId,
} from '../../../selectors/bigStakes';
import Error from '../../EntryComponents/Error';
import SubmissionIcon from '../../Submission/components/Icon';

import styles from './BigStakesBarEntries.module.scss';

// ---

type StoreProps = {
  stake?: RecordOfBigStake;
  error?: RecordOfEntryErrorType | null;
  currency: CurrencyCode;
};

type EntryProps = {
  dialogId: string;
};

const Entry: FC<EntryProps & StoreProps> = ({
  stake,
  error,
  currency,
}) => {
  if (!stake) {
    return null;
  }
  const replacedStakeName = replacingHyphenOnDash(stake.name);

  const replacedStakeDescription = (stake: RecordOfBigStake) => {
    if (stake.description?.length) {
      return replacingHyphenOnDash(stake.description);
    }
    else if (stake.matchNames?.length) {
      const names = stake.matchNames.map((matchName) => replacingHyphenOnDash(matchName)).join('<br />');
      return names;
    }
  };

  return (
    <div className={styles.entry}
         data-test="bigStakeEntry-container">
      <SubmissionIcon filledIcons
                      submission={stake.submission}
                      size={16} />
      <Hint content={replacedStakeDescription(stake)}
            placement="left"
            data-test="bigStakeEntry-hint"
            childrenWrapperClassName={styles.name}>
        {replacedStakeName}
      </Hint>
      <Amount value={stake.amount}
              className={styles.amount}
              currency={currency} />

      {error && (
        <Error error={error}
               currency={currency}
               className={styles.error}
               data-test="big-stake-entry-error" />
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState, { dialogId }: EntryProps): StoreProps => ({
  stake: selectBigStakeByDialogId(state, dialogId),
  error: selectBigStakeErrorByDialogId(state, dialogId),
  currency: selectCurrency(state),
});

export default connect(mapStateToProps)(Entry);
